import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { ReactComponent as EyeCloseIcon } from "../../assets/icons/eyeClose.svg";
import { ReactComponent as EyeOpenIcon } from "../../assets/icons/eyeOpen.svg";
import { useDispatch, useSelector } from "react-redux";
import { resetUserInfo, updatePassword } from "../../services/loginInfo";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PasswordRulesSet,
  userStatus,
} from "../../utils/constant";
import { PasswordRules } from "../../utils/loginFields";
import SuccessIcon from "../../assets/images/Success_tic1x.png";
import FailedIcon from "../../assets/images/cancel3x.png";
import SuccessGradientMessage from "../success-gradient-message";
import SchedulerLogo from "../../assets/icons/scheduler360Logo.svg";
import {Button} from "../../shared/Button";
import { needsPasswordChange } from "../../routes/guards/authUtils";
import { useUpdateUserPasswordMutation } from "../../features/user/userApi.services";

export const ChangePassword = () => {
  const dispatch = useDispatch();
  const userEmail = localStorage.getItem("forgotEmail") || "";
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector((state) => state.userInfo);
  const isFirstTimePasswordUpdate = needsPasswordChange();
  const currentPasswordFromLogin =
    location.state?.current_password ||
    sessionStorage.getItem("pending_current_password") ||
    "";
  const [updateUserPassword, { isLoading: isUpdatingProfilePassword }] =
    useUpdateUserPasswordMutation();
  const [firstTimeSuccess, setFirstTimeSuccess] = useState(false);
  const [firstTimeError, setFirstTimeError] = useState("");
  const [passwordVisibility, setPasswordVisibility] = useState({
    new_password: false,
    confirm_password: false,
  });
  const [verifyPasswordRule, setVerifyPasswordRule] = useState({
    [PasswordRulesSet.character]: false,
    [PasswordRulesSet.upperlowercase]: false,
    [PasswordRulesSet.number]: false,
    [PasswordRulesSet.email]: false,
    [PasswordRulesSet.common]: false,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    getValues,
    clearErrors,
  } = useForm();

  const eightCharacter = (str) =>
    new RegExp(`^.{${PASSWORD_MIN_LENGTH},${PASSWORD_MAX_LENGTH}}$`).test(str);
  const upperLowerCase = (str) => /^(?=.*[a-z])(?=.*[A-Z])\S+$/.test(str);
  const letterSymbol = (str) =>
    // eslint-disable-next-line no-useless-escape
    /[\d!@#$%^&*()_+={}\[\]:;<>,.?\\\/\-]/.test(str);

  const passwordFailRule = () => {
    return (
      !verifyPasswordRule[PasswordRulesSet.character] ||
      !verifyPasswordRule[PasswordRulesSet.upperlowercase] ||
      !verifyPasswordRule[PasswordRulesSet.number] ||
      !verifyPasswordRule[PasswordRulesSet.email] ||
      !verifyPasswordRule[PasswordRulesSet.common]
    );
  };
  const isLoading = userInfo.loading || isUpdatingProfilePassword;
  const isPasswordChangeSuccess =
    (!userInfo.loading && userInfo.status === userStatus.changePassword) ||
    firstTimeSuccess;
  const isbuttonDisabled = () => {
   return !userEmail && passwordFailRule() && getValues("new_password") !== getValues("confirm_password");
   }

  const clearAuthAndRedirectToLogin = (delayMs = 3000) => {
    // Clear JWT so PublicRoute no longer traps the user on /change-password.
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("forgotEmail");
    sessionStorage.removeItem("pending_current_password");
    setTimeout(() => {
      navigate("/login", { replace: true });
    }, delayMs);
  };

  const onSubmit = (data) => {
    const { new_password, confirm_password } = data;
    if (
      new_password !== "" &&
      confirm_password !== "" &&
      new_password !== confirm_password
    ) {
      setError("confirm_password", {
        type: "custom",
        message: "New password and Confirm password should be same",
      });
      return;
    }

    if (passwordFailRule()) {
      setError("common", {
        type: "manual",
        message: "All Conditions must be satisfied",
      });
      return;
    }

    // First-time login: update via authenticated profile password API
    if (isFirstTimePasswordUpdate) {
      if (!currentPasswordFromLogin) {
        setFirstTimeError("Session expired. Please log in again.");
        clearAuthAndRedirectToLogin(1500);
        return;
      }
      if (new_password === currentPasswordFromLogin) {
        setError("new_password", {
          type: "custom",
          message: "New password and Current password should not be same",
        });
        return;
      }

      setFirstTimeError("");
      const updatedData = {
        current_password: currentPasswordFromLogin,
        new_password,
      };
      updateUserPassword(updatedData)
        .then((response) => {
          if (response?.error) {
            const detail =
              response.error?.data?.detail ||
              response.error?.data?.message ||
              "Failed to update password";
            setFirstTimeError(detail);
          } else {
            setFirstTimeSuccess(true);
            clearAuthAndRedirectToLogin();
          }
        })
        .catch(() => {
          setFirstTimeError("Failed to update password");
        });
      return;
    }

    // Forgot-password flow: existing update_pwd API
    if (userEmail !== "" && new_password === confirm_password) {
      dispatch(
        updatePassword({ email: userEmail, new_password: new_password })
      );
    }
  };

  const handleField = (field, value) => {
    const email = userEmail;
    let emailExist = email !== "" && value !== "";
    if (email !== "") {
      const emailValidation = new RegExp(email);
      emailExist = !emailValidation.test(value);
    }
    const eightCharacterExist = eightCharacter(value);
    const upperLowerCaseExist = upperLowerCase(value);
    const letterSymbolExist = letterSymbol(value);

    const passRule = {
      [PasswordRulesSet.character]: eightCharacterExist,
      [PasswordRulesSet.upperlowercase]: upperLowerCaseExist,
      [PasswordRulesSet.number]: letterSymbolExist,
      [PasswordRulesSet.email]: emailExist,
      [PasswordRulesSet.common]:
        eightCharacterExist &&
        upperLowerCaseExist &&
        letterSymbolExist &&
        emailExist,
    };
    setVerifyPasswordRule(passRule);
  };

  useEffect(() => {
    if (location.state?.current_password) {
      sessionStorage.setItem(
        "pending_current_password",
        location.state.current_password
      );
    }
  }, [location.state?.current_password]);

  useEffect(() => {
    // First-time flow requires the password from login. Without it, clear auth
    // so the user can reach /login (PublicRoute would otherwise bounce them back).
    if (isFirstTimePasswordUpdate && !currentPasswordFromLogin) {
      setFirstTimeError("Session expired. Please log in again.");
      clearAuthAndRedirectToLogin(1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!passwordFailRule()) {
      clearErrors("common");
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyPasswordRule]);

  useEffect(() => {
    dispatch(resetUserInfo());
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!userInfo.loading && userInfo.status === userStatus.changePassword) {
      // Clear stale JWT so is_pwd_changed claim cannot loop the user back here.
      clearAuthAndRedirectToLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, navigate]);
  useEffect(() => {
    // Create a style element
    const style = document.createElement('style');
    
    // Add CSS to hide the Edge/IE password reveal button
    style.textContent = `
      input::-ms-reveal,
      input::-ms-clear {
        display: none !important;
      }
      
      input[type="password"]::-webkit-credentials-auto-fill-button {
        visibility: hidden;
        display: none !important;
        pointer-events: none;
        position: absolute;
        right: 0;
      }
    `;
    
    // Append to the document head
    document.head.appendChild(style);
    
    // Clean up when component unmounts
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  return (
    <React.Fragment>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={
          isLoading ||
          userInfo.status === userStatus.login ||
          userInfo.status === userStatus.pending
        }
      >
        {userInfo.status === userStatus.login ||
        userInfo.status === userStatus.pending ? (
          <div className="popup-content w-2/6 md:w-2/4 sm:w-2/4 bg-white flex flex-col gap-4 h-[330px] justify-center items-center">
            <img
              src={
                userInfo.status === userStatus.pending
                  ? FailedIcon
                  : SuccessIcon
              }
              alt="VerifyIcon"
            />
            {userInfo.status !== userStatus.pending && (
              <h2 className="text-black font-semibold text-lg">
                Congratulations!
              </h2>
            )}
            <span style={{ color: "#232323" }}>
              {userInfo.status === userStatus.pending
                ? "Please wait while you password being change"
                : "Your have successfully reset and created a new password"}
            </span>
          </div>
        ) : (
          <CircularProgress color="inherit" />
        )}
      </Backdrop>
      <SuccessGradientMessage
        message="Password changed successfully"
        isBackdropOpen={isPasswordChangeSuccess}
      />
      <div className="w-full flex items-center justify-center">
        <div className="w-4/5 max-w-md mt-20">
            <div className="flex items-center justify-center">
              <div className="flex gap-x-3 items-center mb-3">
                <img
                  className={"w-[250px] h-[100px]"}
                  src={SchedulerLogo}
                  alt="SchedulerLogo"
                ></img>
              </div>
            </div>
          <h4
            className={`${
              isFirstTimePasswordUpdate ? "mb-2" : "mb-6"
            } mt-6 pb-1 text-xl font-semibold defaultTextColor`}
          >
            {isFirstTimePasswordUpdate ? "Update Password" : "Change Password"}
          </h4>
          {isFirstTimePasswordUpdate && (
            <p className="mb-6 text-sm text-gray-600">
              You need to update your password because this is the first time
              you are signing in.
            </p>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {userInfo.error !== "" || firstTimeError ? (
              <div className="pb-7">
                <p className="error" role="alert">
                  {firstTimeError || userInfo.error}
                </p>
                {firstTimeError?.includes("Session expired") && (
                  <button
                    type="button"
                    className="mt-2 text-sm text-background-primary-main underline"
                    onClick={() => clearAuthAndRedirectToLogin(0)}
                  >
                    Go to Login
                  </button>
                )}
              </div>
            ) : null}
            <div className="relative mb-6">
              <label
                htmlFor="new-password"
                className="block tracking-wide text-gray-700 text-xs mb-2"
              >
                New Password
              </label>
              <input
                id="new-password"
                type={`${
                  passwordVisibility.new_password ? "text" : "password"
                }`}
                className="w-full rounded px-3 py-[0.32rem] focus:outline-none border focus:ring-0 focus:border-background-primary-main leading-[2.15] text-[14px] h-[60px]"
                placeholder="Enter new password"
                style={{
                  color: "#232323",
                  border: "1px solid #3E3E3E",
                }}
                {...register("new_password", {
                  required: true,
                })}
                maxLength={PASSWORD_MAX_LENGTH}
                autoComplete="new-password"
                onKeyUp={(e) => handleField("text", e.target.value)}
                aria-invalid={errors.new_password ? "true" : "false"}
              />

              <button
                type="button"
                className="absolute top-7 end-0 p-3.5 rounded-e-md"
                onClick={() =>
                  setPasswordVisibility({
                    ...passwordVisibility,
                    new_password: !passwordVisibility.new_password,
                  })
                }
                aria-label={
                  passwordVisibility.new_password
                    ? "Hide password"
                    : "Show password"
                }
              >
                {passwordVisibility.new_password ? (
                  <EyeOpenIcon aria-hidden="true" />
                ) : (
                  <EyeCloseIcon aria-hidden="true" />
                )}
              </button>
              {errors.new_password?.type === "required" && (
                <p className="error" role="alert">
                  New Password is required
                </p>
              )}
              {errors.new_password?.type === "custom" && (
                <p className="error" role="alert">
                  {errors.new_password?.message}
                </p>
              )}
            </div>

            <div className="relative mb-6">
              <label
                htmlFor="confirm-password"
                className="block tracking-wide text-gray-700 text-xs mb-2"
              >
                Confirm Password
              </label>
              <input
                id="confirm-password"
                type={`${
                  passwordVisibility.confirm_password ? "text" : "password"
                }`}
                className="w-full rounded px-3 py-[0.32rem] focus:outline-none border focus:ring-0 focus:border-background-primary-main leading-[2.15] text-[14px] h-[60px]"
                placeholder="Enter confirm password"
                style={{
                  color: "#232323",
                  border: "1px solid #3E3E3E",
                }}
                {...register("confirm_password", {
                  required: true,
                })}
                maxLength={PASSWORD_MAX_LENGTH}
                autoComplete="new-password"
                aria-invalid={errors.confirm_password ? "true" : "false"}
              />
              {errors.confirm_password?.type === "required" && (
                <p className="error" role="alert">
                  Confirm Password is required
                </p>
              )}

              <button
                type="button"
                className="absolute top-7 end-0 p-3.5 rounded-e-md"
                onClick={() =>
                  setPasswordVisibility({
                    ...passwordVisibility,
                    confirm_password: !passwordVisibility.confirm_password,
                  })
                }
                aria-label={
                  passwordVisibility.confirm_password
                    ? "Hide password"
                    : "Show password"
                }
              >
                {passwordVisibility.confirm_password ? (
                  <EyeOpenIcon aria-hidden="true" />
                ) : (
                  <EyeCloseIcon aria-hidden="true" />
                )}
              </button>
              {errors.confirm_password?.type === "required" ||
                (errors.confirm_password?.type === "custom" && (
                  <p className="error" role="alert">
                    {errors.confirm_password?.type === "custom"
                      ? errors.confirm_password?.message
                      : "Confirm Password is required"}
                  </p>
                ))}
            </div>

            {errors.common?.type === "manual" && (
              <p className="error" role="alert">
                {errors.common?.message}
              </p>
            )}

            {typeof getValues("new_password") !== "undefined" && (
              <div className="pb-3 leading-6">
                <p className="text-[14px] pb-1">Create a password That:</p>
                <ul className="">
                  {PasswordRules.map((rule, index) => {
                    let icon = "\\2022";
                    let pd = 3;
                    let textColor = "#000";
                    if (verifyPasswordRule[rule.key]) {
                      icon = "\\2714\\0020";
                      pd = 2;
                      textColor = "#2563EB"
                    }
                    return (
                      <li
                        key={index}
                        className={`text-[12px] list-none before:content-['${icon}'] before:pr-${pd} before:text-[10px]`}
                        style={{ color: textColor }}
                      >
                        {rule.name}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-center text-center">
              <Button
              btnType="submit"
              btnCategory="primary"
              isDisabled={isbuttonDisabled()}
              btnName="Save New Password"
              btnCls="px-7 pb-3 pt-3 mt-2"
              />
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};
